import Link from 'next/link';
import { ProgressBar } from '../ui';
import { useTranslation } from '../../../lib/i18n';
import { useCreditsWidgetState } from '../../../lib/use-credits-widget-state';
import { useDailyCreditsResetLine, widgetStateResetInputs } from '../../../lib/use-daily-credits-reset-line';
import { useSettingsCardLink } from '../../../lib/use-settings-card-link';
import { creditsWidgetCopyKeys, CreditsWidgetIcon, CreditsWidgetCtaButton } from './CreditsWidgetShared';
import { usePersonalSaleActive } from '../../../lib/use-personal-sale';

/**
 * The drawer/rail credits card's LOW and OUT states (Figma 16346-94984 / 16346-94588). It REPLACES the
 * ordinary credits card (plan name + usage line) in the `AccountCard` slot whenever the account is low
 * or out — `AccountCard` renders this instead of `CreditsCard` when `useCreditsWidgetState` is not
 * `none`, so the two never show at once.
 *
 * Icon + title, the "{used} of {cap} … credits used" line, an always-visible progress bar at the REAL
 * used percent (≈70% when low, full when out — product decision, overriding the Figma OUT frame which
 * omitted the bar), and a full-width CTA ("Upgrade", or "Buy more credits" on Max+). The descriptive
 * body sentence between the title and the usage line is temporarily removed (keys kept — see below).
 *
 * Returns null when the state is `none`; the caller reads the same hook, so this is belt-and-braces.
 */
export function CreditsMenuWidget() {
  const { t } = useTranslation();
  const state = useCreditsWidgetState();
  // Personal sale: while the offer is live the CTA sells the discount ("Upgrade and save 30%"),
  // replacing the old "30% OFF" badge that used to ride this low/out card.
  const saleActive = usePersonalSaleActive();
  // Own read rather than borrowing `useCreditsSummary()`'s: a daily-pool low/out state can fire
  // while the period meter — and so `useCreditsSummary` — is still null.
  const settingsLink = useSettingsCardLink();
  const resetLine = useDailyCreditsResetLine(widgetStateResetInputs(state));
  if (state.level === 'none') return null;
  // `bodyKey` is deliberately NOT destructured/rendered for now — the descriptive sentence between the
  // title and the usage line is temporarily removed (other text is being re-added here soon). The key
  // still exists in the catalog and `creditsWidgetCopyKeys` still returns it, so re-adding is one line.
  const { titleKey, params } = creditsWidgetCopyKeys(state);
  // The "{used} of {cap} … credits used" line above the bar, same as the ordinary card — kept here too
  // (Design: the numeric usage always shows) with the title/body block stacked above it. The pool picks
  // the wording: the daily ceiling reads "daily", the period pool "monthly".
  const usageLabel = t(state.pool === 'daily' ? 'sidebar.credits.usedDaily' : 'sidebar.credits.used', {
    used: state.used.toLocaleString(),
    cap: state.cap.toLocaleString(),
  });

  return (
    <div style={{ position: 'relative', borderRadius: 16 }}>
      {/* The body's hit target — a stretched Link behind the content, same construction as the
          ordinary card (`AccountCard`/`MobileFooter`'s `CreditsCard`), so both variants of the slot
          open Settings the same way. */}
      <Link
        href={settingsLink.route}
        as={settingsLink.path}
        onClick={settingsLink.onClick}
        aria-label={settingsLink.label}
        style={{ position: 'absolute', inset: 0, borderRadius: 16, zIndex: 0 }}
      />
      <div
        // Same fill/track theming as the ordinary card (`.orion-credits-card` carries the dark rule an
        // inline style can't), so this drops into the AccountCard slot with the card look it replaces.
        className="orion-credits-card"
        style={{
          position: 'relative',
          zIndex: 1,
          pointerEvents: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          padding: 16,
          borderRadius: 16,
          color: 'inherit',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span
            style={{
              display: 'flex',
              // Top-align so a title that wraps to two lines (e.g. "Your Monthly credits limit reached" in
              // the narrow rail) keeps the icon beside its first line instead of centering against both.
              alignItems: 'flex-start',
              gap: 8,
              fontSize: 14,
              fontWeight: 'var(--sym-weight-semibold)',
              lineHeight: '18px',
              color: 'var(--ds-text-primary)',
            }}
          >
            <span style={{ display: 'inline-flex', flexShrink: 0, marginTop: 1 }}>
              <CreditsWidgetIcon />
            </span>
            {/* `.orion-sidebar` forces `white-space: nowrap` for its collapse clip, so the title has to
                opt back into wrapping or it overflows the 236px rail (same treatment as the ordinary
                card's usage line). */}
            <span style={{ minWidth: 0, flex: 1, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{t(titleKey, params)}</span>
          </span>
          {/* Daily OUT only, when the reset instant is known and ahead — see `lib/daily-credits-reset.ts`. */}
          {resetLine && (
            <span style={{ fontSize: 14, lineHeight: '18px', color: 'var(--ds-text-primary)', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
              {resetLine}
            </span>
          )}
        </div>
        <ProgressBar progress="neutral" value={state.usedPct} label={usageLabel} decorative />
        <span style={{ pointerEvents: 'auto', display: 'block' }}>
          <CreditsWidgetCtaButton fullWidth label={saleActive ? t('personalSale.creditsCta') : undefined} />
        </span>
      </div>
    </div>
  );
}
